import { FC, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { IndividualSignUpFormValidationSchema } from '@features/SignUp/signUpFormValidation.ts';
import {
  attachPaymentAndSubscribe,
  individualSignUp,
  verifyUser,
  resendVerificationCode,
  checkEmailAvailability,
} from '@api/auth/apiService.ts';
import {
  IndividualSignUpRequestData,
  VerifyUserResponse,
} from '@api/auth/model/types.ts';
import { saveStripeInfoDuringSignup } from '@api/user/apiService.ts';
import toast from 'react-hot-toast';
import { Toast } from '@components/Toast/ui/Toast.tsx';
import { FormInput } from '@components/FormInput/ui/FormInput';
import { Button, TermsPrivacyModal } from '@components/index';
import InputCheckbox from '@components/InputCheckbox/ui/InputCheckbox';
import { checkPasswordComplexity } from '@utils/utils';
import { getApiErrorMessage } from '@shared/utils/handle-backend-error';
import { isInternalEmail } from '@shared/utils/internal-email';
import { SignUpFormDefaultValues } from '@providers/SignUpFormProvider/model/types';
import { Box, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
// removed useTheme import as hyperlink styling is no longer needed
import AddPaymentMethodModal from '@features/AddPaymentMethodModal/ui/AddPaymentMethodModal';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCaseSubscriptionPlans } from '@shared/hooks/useCaseSubscriptionPlans';
import { useSignUpFormContext } from '@providers/SignUpFormProvider/model/SignUpFormContext';
import apiHandler from '@api/ApiHandler';
import { object } from 'yup';
import * as yup from 'yup';
import styles from './IndividualSignUpForm.module.scss';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

// Same shape the signup resolver validates with, so the debounced availability
// check and the form agree on when an address is worth querying.
const EMAIL_PATTERN = /^[\w.-]+(\+[\w.-]+)?@[a-zA-Z0-9-]+\.[a-zA-Z]{2,4}$/;

interface IIndividualSignUpFormProps {}

interface IndividualSignUpForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

const IndividualSignUpForm: FC<IIndividualSignUpFormProps> = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);
  const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);
  const [pendingSignUpData, setPendingSignUpData] =
    useState<IndividualSignUpRequestData | null>(null);
  const [showVerificationStep, setShowVerificationStep] =
    useState<boolean>(false);
  // Set when signup is rejected because the email is already registered, so the
  // form can offer a route back in rather than a dead end. See ZLA-002.
  const [existingAccount, setExistingAccount] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isResendingCode, setIsResendingCode] = useState<boolean>(false);
  const { setSignUpFormState } = useSignUpFormContext();
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  // removed theme usage
  const { trialPlan } = useCaseSubscriptionPlans();
  const trialDurationDays = trialPlan?.trial_duration_days ?? 30;

  const formMethods = useForm<IndividualSignUpForm>({
    mode: 'onChange',
    defaultValues: {
      firstName: SignUpFormDefaultValues.firstName,
      lastName: SignUpFormDefaultValues.lastName,
      password: SignUpFormDefaultValues.password,
      confirmPassword: SignUpFormDefaultValues.confirmPassword,
      email: SignUpFormDefaultValues.email,
      phone: '',
      acceptTerms: SignUpFormDefaultValues.acceptTerms,
    },
    resolver: yupResolver(IndividualSignUpFormValidationSchema),
  });

  const password = formMethods.watch('password');
  const emailValue = formMethods.watch('email');

  // Surface a taken email while the user is still on the field, rather than after
  // they submit and are told the address is gone. Debounced because the endpoint is
  // throttled, and only run once the address is well formed so we are not querying
  // on every keystroke of a half-typed address. See ZLA-002.
  useEffect(() => {
    if (!emailValue || !EMAIL_PATTERN.test(emailValue)) {
      setExistingAccount(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      checkEmailAvailability(emailValue).then((available) => {
        if (!cancelled) setExistingAccount(!available);
      });
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emailValue]);
  // acceptTerms watch removed; acceptance is handled via modal

  // removed hyperlink open handler; modal opens via checkbox only

  const handleAcceptTerms = () => {
    formMethods.setValue('acceptTerms', true, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setOpenTermsModal(false);
  };

  const handleCloseTerms = () => {
    setOpenTermsModal(false);
    formMethods.setValue('acceptTerms', false, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleTermsCheckboxChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    const { checked } = event.target;
    if (checked) {
      // Require explicit acceptance via modal
      formMethods.setValue('acceptTerms', false, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setOpenTermsModal(true);
    } else {
      formMethods.setValue('acceptTerms', false, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  // Verification form
  const verificationFormMethods = useForm<{ verifyCode: string }>({
    mode: 'onChange',
    defaultValues: {
      verifyCode: '',
    },
    resolver: yupResolver(
      object().shape({
        verifyCode: yup.string().required('Verification code is required'),
      })
    ),
  });

  const onSubmit = (data: IndividualSignUpForm) => {
    console.log('[IndividualSignUpForm] onSubmit called with data:', {
      email: data.email,
      firstName: data.firstName,
    });

    const signUpData: IndividualSignUpRequestData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      password: data.password,
    };

    console.log(
      '[IndividualSignUpForm] Calling individualSignUp to create user and send verification code'
    );
    setLoading(true);

    // First, create the user account (this will send verification code)
    individualSignUp(
      signUpData,
      (response) => {
        console.log(
          '[IndividualSignUpForm] individualSignUp success, response:',
          response
        );
        console.log(
          '[IndividualSignUpForm] Verification code should have been sent to:',
          signUpData.email
        );

        // Save signup data to context for verification step
        setSignUpFormState({
          ...SignUpFormDefaultValues,
          email: signUpData.email,
          firstName: signUpData.firstName,
          lastName: signUpData.lastName,
          password: signUpData.password,
        });

        // Save pending signup data for later payment step
        setPendingSignUpData(signUpData);

        // Show verification step instead of payment modal
        setShowVerificationStep(true);
        setLoading(false);

        toast(({ id }) => (
          <Toast
            id={id}
            type="success"
            title="Verification Code Sent"
            text={`We've sent a verification code to ${signUpData.email}. Please check your email.`}
          />
        ));
      },
      (error: any) => {
        console.error('[IndividualSignUpForm] individualSignUp error:', error);
        setLoading(false);

        // The account record is written at this step, before payment, so anyone
        // who abandoned an earlier attempt already owns this email address. The
        // only way back in is to sign in, so say that rather than dead-ending on
        // "user already exists".
        const alreadyExists =
          error?.response?.data?.code === 'USER_ALREADY_EXISTS' ||
          error?.response?.status === 409;

        if (alreadyExists) {
          setExistingAccount(true);
          toast(({ id }) => (
            <Toast
              id={id}
              type="error"
              title="Account already exists"
              text="Sign in to continue, or reset your password if you have forgotten it."
            />
          ));
          return;
        }

        const errorMessage = getApiErrorMessage(
          error,
          'Failed to create account. Please try again.'
        );
        toast(({ id }) => (
          <Toast id={id} type="error" title="Error" text={errorMessage} />
        ));
      }
    );
  };

  const handleVerificationSubmit = (verificationData: {
    verifyCode: string;
  }) => {
    console.log(
      '[IndividualSignUpForm] handleVerificationSubmit called with code:',
      verificationData.verifyCode
    );

    if (!pendingSignUpData) {
      console.error('[IndividualSignUpForm] No pending signup data found');
      toast(({ id }) => (
        <Toast
          id={id}
          type="error"
          title="Error"
          text="Signup data not found. Please start over."
        />
      ));
      return;
    }

    setIsVerifying(true);
    console.log(
      '[IndividualSignUpForm] Calling verifyUser with email:',
      pendingSignUpData.email
    );

    verifyUser(
      {
        email: pendingSignUpData.email,
        code: verificationData.verifyCode,
      },
      (data: VerifyUserResponse) => {
        console.log(
          '[IndividualSignUpForm] verifyUser success, response:',
          data
        );
        console.log(
          '[IndividualSignUpForm] Email verified successfully, storing tokens and showing payment modal'
        );

        // Store access and refresh tokens in localStorage directly (without triggering AuthProvider state)
        // This ensures authenticated API calls work for à la carte plan checkout without navigation side effects
        if (data.access && data.refresh) {
          localStorage.setItem('accessToken', JSON.stringify(data.access));
          localStorage.setItem('refreshToken', JSON.stringify(data.refresh));
          // Update ApiHandler with the new token so authenticated requests work immediately
          apiHandler.setAuthToken(data.access);
          console.log(
            '[IndividualSignUpForm] Tokens stored successfully in localStorage and ApiHandler updated'
          );
        }

        setIsVerifying(false);
        setShowVerificationStep(false);

        // Firm staff are not customers: the backend has already given them free
        // unlimited access and put them in the firm, so showing a plan picker or
        // asking for a card would be wrong. Send them straight to sign in.
        if (isInternalEmail(pendingSignUpData.email)) {
          toast(({ id }) => (
            <Toast
              id={id}
              type="success"
              title="Account Ready"
              text="Your email has been verified. Please log in to start using Zlayt AI."
            />
          ));
          navigate('/sign-in');
          return;
        }

        toast(({ id }) => (
          <Toast
            id={id}
            type="success"
            title="Email Verified"
            text="Your email has been verified. Please proceed with payment."
          />
        ));

        // Now show payment modal
        setOpenPaymentModal(true);
      },
      (error: any) => {
        console.error('[IndividualSignUpForm] verifyUser error:', error);
        setIsVerifying(false);
        const errorMessage = getApiErrorMessage(
          error,
          'Invalid verification code. Please try again.'
        );
        // Put the message on the field itself. A toast in the corner is easy to miss
        // and does not say where the problem is; the user stays on this screen and
        // simply retypes the code.
        verificationFormMethods.setError('verifyCode', {
          type: 'server',
          message: errorMessage,
        });
      }
    );
  };

  const handleResendVerificationCode = () => {
    if (!pendingSignUpData) {
      console.error(
        '[IndividualSignUpForm] Cannot resend code: no pending signup data'
      );
      return;
    }

    console.log(
      '[IndividualSignUpForm] Resending verification code to:',
      pendingSignUpData.email
    );
    setIsResendingCode(true);

    resendVerificationCode(
      pendingSignUpData.email,
      () => {
        console.log(
          '[IndividualSignUpForm] Verification code resent successfully'
        );
        setIsResendingCode(false);
        toast(({ id }) => (
          <Toast
            id={id}
            type="success"
            title="Code Resent"
            text="We've resent the verification code to your email."
          />
        ));
      },
      (error: any) => {
        console.error(
          '[IndividualSignUpForm] Resend verification code error:',
          error
        );
        setIsResendingCode(false);
        const errorMessage = getApiErrorMessage(
          error,
          'Failed to resend code. Please try again.'
        );
        toast(({ id }) => (
          <Toast id={id} type="error" title="Error" text={errorMessage} />
        ));
      }
    );
  };

  const handlePaymentCancel = () => {
    console.log(
      '[IndividualSignUpForm] handlePaymentCancel called, closing payment modal'
    );
    setOpenPaymentModal(false);
  };

  const handlePaymentConfirm = (
    paymentMethodId: string,
    selectedPlanPriceId?: number | null
  ): Promise<void> => {
    console.log('[IndividualSignUpForm] handlePaymentConfirm called with:', {
      paymentMethodId,
      selectedPlanPriceId,
      hasPendingData: !!pendingSignUpData,
    });

    // The payment modal deliberately stays open until this resolves. Closing it
    // here meant any failure dropped the user onto the signup form underneath,
    // which they cannot resubmit because the account already exists. That is what
    // read as "payment redirects to Create an Account". On success we navigate
    // away, which dismisses the modal anyway. See ZLA-010.

    // Check if this is an à la carte purchase (indicated by selectedPlanPriceId being null)
    const isAlaCartePurchase = selectedPlanPriceId === null;

    return new Promise((resolve, reject) => {
      if (pendingSignUpData) {
        console.log(
          '[IndividualSignUpForm] User already created and verified, attaching payment'
        );
        console.log(
          '[IndividualSignUpForm] isAlaCartePurchase:',
          isAlaCartePurchase,
          'selectedPlanPriceId:',
          selectedPlanPriceId
        );

        if (isAlaCartePurchase) {
          // For à la carte purchases, the credits are already granted by the Payment Intent webhook
          // We just need to save the payment method and redirect
          console.log(
            '[IndividualSignUpForm] À la carte purchase - saving payment method and redirecting'
          );
          setLoading(true);

          saveStripeInfoDuringSignup(
            pendingSignUpData.email,
            paymentMethodId,
            () => {
              console.log(
                '[IndividualSignUpForm] Payment method saved for à la carte purchase'
              );
              // resolve here so AddPaymentMethodModal can proceed with PI confirmation
              setLoading(false);
              resolve();
            },
            (error: any) => {
              console.error(
                '[IndividualSignUpForm] Error saving payment method for à la carte:',
                error
              );
              setLoading(false);
              reject(error);
            },
            () => {}
          );
          setPendingSignUpData(null);
          return;
        }

        // For subscription plans (trial or paid), attach payment and create subscription
        console.log(
          '[IndividualSignUpForm] Subscription purchase - attaching payment and creating subscription'
        );
        setLoading(true);

        if (typeof selectedPlanPriceId !== 'number') {
          // No plan/trial selected. Prevent auto subscription.
          toast(({ id }) => (
            <Toast
              id={id}
              type="error"
              title="Select a plan"
              text="Please select a subscription plan or start the free trial before continuing."
            />
          ));
          setLoading(false);
          reject(new Error('No plan selected'));
          return;
        }

        // User already exists and is verified - just attach payment method and create subscription
        attachPaymentAndSubscribe(
          pendingSignUpData.email,
          paymentMethodId,
          () => {
            console.log(
              '[IndividualSignUpForm] attachPaymentAndSubscribe success'
            );
            const isTrial = selectedPlanPriceId === -1;
            const successText = isTrial
              ? `Your account has been successfully created with a ${trialDurationDays}-day free trial. Please log in to start using Zlayt AI.`
              : 'Your subscription has been activated successfully. Please log in to start using Zlayt AI.';
            toast(
              ({ id }) => (
                <Toast
                  id={id}
                  type="success"
                  title="Success"
                  text={successText}
                />
              ),
              {
                duration: 10000,
              }
            );
            setLoading(false);
            console.log('[IndividualSignUpForm] Navigating to sign-in page');
            navigate('/sign-in');
            resolve();
          },
          (error: any) => {
            console.error(
              '[IndividualSignUpForm] attachPaymentAndSubscribe error:',
              error
            );
            const errorMessage = getApiErrorMessage(
              error,
              'Failed to complete subscription. Please try again.'
            );
            toast(({ id }) => (
              <Toast
                id={id}
                type="error"
                title="Payment Error"
                text={errorMessage}
              />
            ));
            setLoading(false);
            reject(error);
          },
          selectedPlanPriceId
        );
        setPendingSignUpData(null);
      } else {
        console.error(
          '[IndividualSignUpForm] handlePaymentConfirm called but no pending signup data'
        );
        reject(new Error('No pending signup data'));
      }
    });
  };

  useEffect(() => {
    console.log('[IndividualSignUpForm] Component mounted');
    return () => {
      console.log('[IndividualSignUpForm] Component unmounting');
    };
  }, []);

  useEffect(() => {
    if (!password) return;
    formMethods.trigger('confirmPassword');
  }, [password]);

  useEffect(() => {
    if (openPaymentModal) {
      console.log('[IndividualSignUpForm] Payment modal opened');
    }
  }, [openPaymentModal]);

  useEffect(() => {
    if (showVerificationStep) {
      console.log('[IndividualSignUpForm] Verification step shown');
    }
  }, [showVerificationStep]);

  // Show verification step if needed
  if (showVerificationStep) {
    console.log('[IndividualSignUpForm] Rendering verification step');
    return (
      <>
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <Grid item xs={12}>
            <Grid
              container
              direction={{ xs: 'column-reverse', md: 'row' }}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  paddingX={3}
                >
                  <Typography
                    color="gray.main"
                    gutterBottom
                    variant={downMD ? 'h3' : 'h2'}
                    textAlign="center"
                  >
                    Verify your email
                  </Typography>
                  <Typography
                    color="gray"
                    gutterBottom
                    fontSize={14}
                    variant="subtitle2"
                    textAlign="center"
                  >
                    We've sent a verification code to{' '}
                    {pendingSignUpData?.email || 'your email'}. Please enter it
                    below.
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <form
              onSubmit={verificationFormMethods.handleSubmit(
                handleVerificationSubmit
              )}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormInput
                    bordered
                    type="input"
                    labelText="Verification Code"
                    required
                    placeholder="Enter verification code"
                    errorText={
                      verificationFormMethods.formState.errors.verifyCode
                        ?.message
                    }
                    {...verificationFormMethods.register('verifyCode')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {/* type="button" matters here. A <button> inside a form defaults
                        to type="submit", and pressing Enter in the code field triggers
                        implicit submission, which activates the FIRST submit button in
                        the form. That was this Back button, so hitting Enter threw the
                        user back to the signup screen instead of verifying the code. */}
                    <Button
                      type="button"
                      variant="text"
                      onClick={() => {
                        console.log(
                          '[IndividualSignUpForm] Back button clicked, returning to signup form'
                        );
                        setShowVerificationStep(false);
                        verificationFormMethods.reset();
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      onClick={handleResendVerificationCode}
                      disabled={isResendingCode}
                    >
                      {isResendingCode ? 'Resending...' : 'Resend Code'}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    className={styles.signUpBtn}
                    disabled={!verificationFormMethods.formState.isValid}
                    variant="active"
                    isLoading={isVerifying}
                  >
                    Verify Email
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </>
    );
  }

  return (
    <>
      <Grid container spacing={2} alignItems="center" justifyContent="center">
        <Grid item xs={12}>
          <Grid
            container
            direction={{ xs: 'column-reverse', md: 'row' }}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item>
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1}
                paddingX={3}
              >
                <Typography
                  color="gray.main"
                  gutterBottom
                  variant={downMD ? 'h3' : 'h2'}
                  textAlign="center"
                >
                  Create an account
                </Typography>
                <Typography
                  color="gray"
                  gutterBottom
                  fontSize={14}
                  variant="subtitle2"
                  textAlign="center"
                >
                  Sign up to ZlaytAI as an individual user.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    bordered
                    type="input"
                    labelText="First name"
                    required
                    placeholder="Enter first name"
                    errorText={formMethods.formState.errors.firstName?.message}
                    autoComplete
                    {...formMethods.register('firstName')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    bordered
                    type="input"
                    labelText="Last name"
                    required
                    placeholder="Enter last name"
                    errorText={formMethods.formState.errors.lastName?.message}
                    autoComplete
                    {...formMethods.register('lastName')}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  bordered
                  type="input"
                  labelText="Email"
                  required
                  placeholder="name@company.com"
                  errorText={
                    formMethods.formState.errors?.email &&
                    formMethods.formState.errors?.email.message
                  }
                  {...formMethods.register('email')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  bordered
                  type="input"
                  labelText="Phone number"
                  required
                  placeholder=" +1 (555) 000-0000"
                  inputMode="tel"
                  pattern="[+0-9 ()-]+"
                  errorText={formMethods.formState.errors.phone?.message}
                  {...formMethods.register('phone')}
                />
              </Grid>
              <Grid item xs={12}>
                <div className={styles.passwordComplexityWrapper}>
                  <FormInput
                    bordered
                    type="password"
                    labelText="Password"
                    required
                    placeholder="Create password"
                    errorText={
                      formMethods.formState.errors?.password &&
                      formMethods.formState.errors?.password.message
                    }
                    {...formMethods.register('password')}
                  />
                  <div
                    className={[
                      styles.complexity,
                      styles[`level-${checkPasswordComplexity(password)}`],
                    ].join(' ')}
                  >
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                  <p className={styles.description}>
                    Use 8+ characters, with a mix of letters, numbers & symbols.
                  </p>
                </div>
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  bordered
                  type="password"
                  labelText="Confirm password"
                  required
                  placeholder="Confirm password"
                  errorText={
                    formMethods.formState.errors?.confirmPassword &&
                    formMethods.formState.errors?.confirmPassword.message
                  }
                  {...formMethods.register('confirmPassword')}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <InputCheckbox
                    error={!!formMethods.formState.errors.acceptTerms?.message}
                    {...formMethods.register('acceptTerms')}
                    onChange={handleTermsCheckboxChange}
                  />
                  <p>I Accept the Terms of Service and Privacy Policy</p>
                </Box>
              </Grid>
              {existingAccount && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'warning.main',
                      backgroundColor: 'warning.lighter',
                    }}
                  >
                    <Typography variant="body2">
                      An account with this email already exists.{' '}
                      <Link to="/sign-in">Sign in to continue</Link>, or{' '}
                      <Link to="/forgot-password">reset your password</Link> if
                      you have forgotten it.
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  className={styles.signUpBtn}
                  disabled={!formMethods.formState.isValid}
                  variant="active"
                  isLoading={loading}
                >
                  Continue
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Grid item container direction="column" alignItems="center" xs={12}>
            <Stack
              display="flex"
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
            >
              <Typography variant="subtitle1" sx={{ textDecoration: 'none' }}>
                Already have an account?
              </Typography>
              <Link to="/sign-in">
                <Button variant="underline">Login</Button>
              </Link>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <TermsPrivacyModal
        open={openTermsModal}
        onAccept={handleAcceptTerms}
        onClose={handleCloseTerms}
        termsDocxUrl="/legal/Terms of Use - Zlayt.AI.docx"
        privacyDocxUrl="/legal/Zlate Privacy Policy.docx"
      />
      <Elements stripe={stripePromise}>
        {openPaymentModal && (
          <AddPaymentMethodModal
            showModal={openPaymentModal}
            onCancel={handlePaymentCancel}
            onConfirm={(
              paymentMethodId: string,
              selectedPlanPriceId?: number | null
            ) => handlePaymentConfirm(paymentMethodId, selectedPlanPriceId)}
            saveDirectly={false}
          />
        )}
      </Elements>
    </>
  );
};

export default IndividualSignUpForm;
